import { Link } from "react-router-dom";
import { ArrowLeft, Radar } from "lucide-react";

const UPDATED = "June 2026";

function LegalShell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#080B11] text-slate-300">
      <header className="border-b border-[#232D42] px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#00E5A3] flex items-center justify-center"><Radar size={18} className="text-[#04140F]" /></div>
          <span className="font-heading font-extrabold text-lg text-slate-100">Sideline<span className="text-[#00E5A3]">IQ</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14} /> Home</Link>
      </header>
      <div className="max-w-[800px] mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading text-3xl font-extrabold text-slate-50 mb-1">{title}</h1>
        <p className="text-xs font-mono text-slate-500 mb-8">Last updated: {UPDATED}</p>
        <div className="space-y-6 text-sm leading-relaxed text-slate-400">{children}</div>
        <p className="text-[11px] text-slate-600 mt-10 border-t border-[#232D42] pt-6">
          This document is provided as a general template and does not constitute legal advice.
          Consult a qualified attorney to ensure compliance in your jurisdiction before launch.
        </p>
      </div>
    </div>
  );
}

function H({ children }) {
  return <h2 className="font-heading text-lg font-bold text-slate-100 mb-2">{children}</h2>;
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <section data-testid="terms-disclaimer" className="rounded-xl border border-[#F5A524]/30 bg-[#F5A524]/5 p-4">
        <H>Informational Use Only — Not Betting Advice</H>
        <p>SidelineIQ is an informational and research platform. We aggregate publicly available sports
          information (player status, injuries, transactions, schedules, news and odds) and present it with
          source attribution. <span className="text-slate-200 font-semibold">We do not provide betting
          recommendations, picks, guarantees, or financial advice.</span> Any decision you make is your own.</p>
      </section>
      <section><H>1. Eligibility</H><p>You must be of legal age (18+, or 21+ where required for sports wagering)
        in your jurisdiction to use SidelineIQ. By using the service you represent that you meet this requirement
        and that use of sports-information services is lawful where you live.</p></section>
      <section><H>2. Accounts</H><p>You are responsible for maintaining the confidentiality of your account and
        for all activity under it. You may sign in with email/password or a supported third-party provider (e.g.
        Google). Do not share your credentials.</p></section>
      <section><H>3. Subscriptions & Billing</H><p>Paid tiers are billed through our payment processor. Prices,
        features and delays for each tier are described on the pricing page. You may cancel a recurring
        subscription at any time; access continues until the end of the paid period. Lifetime/founder purchases
        are one-time and non-recurring.</p></section>
      <section><H>4. No Warranty on Data</H><p>Sports data changes rapidly and is sourced from third parties.
        We strive for accuracy and timeliness but do not warrant that information is complete, current, or
        error-free. Always verify official (league/team-confirmed) versus reported (media) information before
        acting. Free tiers receive data on a delay.</p></section>
      <section><H>5. Acceptable Use</H><p>You may not scrape, resell, or redistribute our data, attempt to
        disrupt the service, or use it for unlawful purposes.</p></section>
      <section><H>6. Responsible Gambling</H><p>If you choose to wager elsewhere, please do so responsibly. If you
        or someone you know has a gambling problem, call <span className="text-[#00E5A3]">1-800-GAMBLER</span>,
        text the same number, or visit ncpgambling.org (U.S.) / ConnexOntario 1-866-531-2600 (Canada). See our{" "}
        <Link to="/responsible-gaming" className="text-[#00E5A3] underline">Responsible Gaming resources</Link>.</p></section>
      <section><H>7. Limitation of Liability</H><p>To the maximum extent permitted by law, SidelineIQ is not
        liable for any losses (including gambling losses) arising from use of the service. The service is provided
        "as is."</p></section>
      <section data-testid="terms-trademarks"><H>8. Trademarks & Affiliation</H><p>SidelineIQ is an independent
        information service. We are <span className="text-slate-200 font-semibold">not affiliated with, endorsed
        by, sponsored by, or licensed by</span> the NFL, NBA, NHL, MLB, ESPN, any team, any sportsbook, or any
        data provider. All team names, logos and trademarks are the property of their respective owners and are
        used for identification and informational purposes only.</p></section>
      <section><H>9. Governing Law</H><p>These terms are governed by the laws of the State of Delaware, USA, without
        regard to conflict-of-law rules, and any dispute will be resolved in the state or federal courts located
        there, unless a mandatory local consumer law provides otherwise. (Confirm the governing jurisdiction with
        your attorney before launch.)</p></section>
      <section><H>10. Intellectual Property & DMCA</H><p>If you believe content on SidelineIQ infringes your
        copyright, send a notice with the required details (identification of the work, the material, your contact
        info, and a good-faith statement) to <span className="text-slate-200">dmca@sidelineiq.io</span>. We will
        respond in accordance with the DMCA.</p></section>
      <section><H>11. Changes</H><p>We may update these terms; continued use after changes constitutes acceptance.</p></section>
      <section><H>12. Contact</H><p>Questions? Reach us at support@sidelineiq.io.</p></section>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <section><H>1. Information We Collect</H><p>Account information you provide (email, and for social sign-in,
        your name and email from the provider), your watchlists and preferences, and basic usage/technical data
        (device, log data) needed to run the service.</p></section>
      <section><H>2. How We Use It</H><p>To provide and improve the service, personalize alerts and watchlists,
        process subscriptions, and communicate with you about your account.</p></section>
      <section><H>3. Guest & Local Data</H><p>You can browse as a guest. Guest watchlists and preferences are
        stored locally on your device and are not sent to us until you create an account.</p></section>
      <section><H>4. Payments</H><p>Payments are processed by our third-party payment processor. We do not store
        your full card details on our servers.</p></section>
      <section><H>5. Third-Party Data</H><p>Sports data is provided by third-party sources (e.g. ESPN, odds
        providers). Links to sportsbooks or sources are provided for reference; those sites have their own
        privacy practices.</p></section>
      <section><H>6. Cookies</H><p>We use essential cookies/local storage for authentication and preferences.</p></section>
      <section data-testid="privacy-rights"><H>7. Your Privacy Rights (US & Canada)</H><p>Depending on where you
        live (including under the CCPA/CPRA in California, other U.S. state laws, and Canada's PIPEDA / Québec Law 25),
        you may request access to, correction of, a portable copy of, or deletion of your personal information. You can
        do this yourself anytime from your <Link to="/account" className="text-[#00E5A3] underline">Account &amp; Privacy</Link>{" "}
        page (export or delete), or by emailing privacy@sidelineiq.io. We do not discriminate against you for exercising these rights.</p></section>
      <section data-testid="privacy-do-not-sell"><H>8. Do Not Sell or Share My Info</H><p>SidelineIQ
        <span className="text-slate-200 font-semibold"> does not sell or share</span> your personal information with third parties for
        cross-context behavioral advertising or monetary consideration. There is nothing to opt out of — but you remain in full control
        of your data via the Account &amp; Privacy page.</p></section>
      <section><H>9. Data Retention &amp; Security</H><p>We retain data as long as your account is active and apply
        reasonable safeguards to protect it. When you delete your account, we remove your profile, watchlists and
        alerts. No method of transmission is 100% secure.</p></section>
      <section><H>10. Children</H><p>SidelineIQ is not intended for anyone under 18.</p></section>
      <section><H>11. Contact</H><p>Privacy questions? Email privacy@sidelineiq.io.</p></section>
    </LegalShell>
  );
}
